import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { supabase } from "../../supabaseClient";
import { getAllUsers } from "../../Services/Users";
import { getAllProjects } from "../../Services/Project";
import {
  getAllTransferHistory,
  getUserTransferHistory,
  getProjectTransferHistory,
  getCpSourceTransferHistory,
  getBatchTransferHistory,
} from "../../Services/WorkTransferApis";
import WorkTransferNotice from "../../Components/WorkTransfer/WorkTransferNotice";
import { LeadRow } from "../../Components/WorkTransfer/LeadDetailsPanel";
import { COLORS, PageHeader, Field, PrimaryButton, SecondaryButton, fieldSx, cardSx } from "../../Components/WorkTransfer/wtStyle";

// Phase 6 — FR-16/17/18 audit views. Built as the module's own page rather
// than a new tab inside the existing Users/Project pages — FR-17/18's exact
// placement inside those pages was never decided (Phase 0 table item 6), so
// this satisfies the functional requirement (view any user/project/CP's
// history) without touching those pages' IA ahead of that decision.
//
// UNTESTABLE against real rows until the Phase 1 SQL is run and at least one
// real transfer has executed — WorkTransferLogs doesn't exist yet.

const TABS = ["All Transfers", "By User", "By Project", "By CP Source"];

const HISTORY_HEADERS = ["When", "Entity", "Entity type", "From", "To", "Reason", "Batch"];

function InfoBit({ label, value }) {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography fontSize={10.5} color={COLORS.textSecondary} sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography fontSize={12.5} fontWeight={600}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

function HistoryTable({ rows, onOpenBatch, onOpenRow }) {
  if (!rows) return null;
  if (rows.length === 0) {
    return (
      <Typography fontSize={14} color={COLORS.textSecondary} sx={{ py: 5, textAlign: "center" }}>
        No transfers found.
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: COLORS.sunken, height: 48 }}>
            {HISTORY_HEADERS.map((h) => (
              <TableCell key={h} sx={{ fontSize: 14, fontWeight: 700, color: COLORS.textSecondary, borderBottom: `1px solid ${COLORS.border}`, bgcolor: COLORS.sunken }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.id}
              hover
              onClick={() => onOpenRow(r)}
              sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" }, "& td": { borderBottom: `1px solid ${COLORS.border}`, fontSize: 14, py: 1.6 } }}
            >
              <TableCell>{new Date(r.created_at).toLocaleString()}</TableCell>
              <TableCell>
                {r.leadName || r.leadDisplayId || r.cpName || r.cpDisplayId || (r.entityId ? r.entityId.slice(0, 8) : "—")}
                {r.leadDisplayId && r.leadName && (
                  <Typography component="div" fontSize={11.5} color={COLORS.textSecondary}>
                    {r.leadDisplayId}
                  </Typography>
                )}
                {r.cpDisplayId && r.cpName && (
                  <Typography component="div" fontSize={11.5} color={COLORS.textSecondary}>
                    {r.cpDisplayId}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip label={r.entityType} size="small" sx={{ fontSize: 12, fontWeight: 500, bgcolor: COLORS.blueLight, color: COLORS.blue }} />
                {r.direction && (
                  <Chip
                    label={r.direction}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 0.5, fontSize: 12, fontWeight: 500, borderColor: COLORS.border }}
                  />
                )}
              </TableCell>
              <TableCell>{r.fromUserName || r.fromCpName || r.fromUserId || r.fromCpId || "—"}</TableCell>
              <TableCell>{r.toUserName || r.toCpName || r.toUserId || r.toCpId || "—"}</TableCell>
              <TableCell>{r.note || "—"}</TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>{r.transferBatchId?.slice(0, 8)}</span>
                  <Typography
                    component="span"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenBatch(r.transferBatchId);
                    }}
                    sx={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, color: COLORS.blue, cursor: "pointer", textDecoration: "underline", flexShrink: 0 }}
                  >
                    View all
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Click a batch id to see every individual record that batch touched — one
// WorkTransferLogs row per lead (or request), all sharing this batch id.
// Lead-shaped rows (Lead/SiteVisit/LeadSourceCP) are enriched server-side
// with the SAME rich fields the bucket "View leads" panels show (project,
// unit type, verification, CP/FOS, etc. — auditHistory.js
// attachFullLeadDetailsToBatchRows), rendered one lead per row via the same
// LeadRow component those panels use. Rows without lead data fall back to a
// plain name/id+type+date line — a CPRelationship row shows the reassigned
// CP's own name/id (server-enriched via relationshipCpId, see
// auditHistory.js's enrichCpRelationshipRows); anything else (e.g. a
// Cancellation/Unit-Shift approval carry-forward, whose entityId is a
// request-table id, not a leadId) falls back to the bare entityId.
function BatchDetailDialog({ batchId, loading, rows, onClose }) {
  return (
    <Dialog open={!!batchId} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>
        Batch <span style={{ fontFamily: "monospace", fontWeight: 500 }}>{batchId?.slice(0, 8)}</span>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography fontSize={14} color={COLORS.textSecondary} sx={{ py: 2, px: 3 }}>
            No records found for this batch.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {rows.map((r) =>
              r.leadname || r.leaddisplayid ? (
                <LeadRow key={r.id} lead={r} bucketName={null} />
              ) : (
                <Box key={r.id} sx={{ py: 1.2, px: 3, borderTop: `1px solid ${COLORS.border}` }}>
                  <Typography fontSize={13.5} fontWeight={600}>
                    {r.cpName || r.cpDisplayId || r.entityId || "—"}
                  </Typography>
                  <Typography fontSize={12} color={COLORS.textSecondary}>
                    {r.entityType} · {new Date(r.created_at).toLocaleString()}
                  </Typography>
                </Box>
              )
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </DialogActions>
    </Dialog>
  );
}

// Clicking a single history row shows just that one record's detail — not
// every lead/CP in its batch (that's what the "Batch" column's "View all"
// link is for). Reads straight off the row already loaded in the main table
// (leadDisplayId/leadType come from auditHistory.js's enrichLeadRows,
// cpName/cpDisplayId from enrichCpRelationshipRows, oldLeadStatus is a plain
// column on the row) — no second fetch, so there's no batch data involved
// anywhere in this path. CPRelationship rows have no lead at all, so they
// get their own CP-shaped detail instead of the lead fields.
function RowDetailDialog({ row, onClose }) {
  const isCpRelationship = row?.entityType === "CPRelationship";
  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>{isCpRelationship ? "CP detail" : "Lead detail"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {isCpRelationship ? (
            <>
              <InfoBit label="CP name" value={row?.cpName} />
              <InfoBit label="CP ID" value={row?.cpDisplayId} />
            </>
          ) : (
            <>
              <InfoBit label="CUID" value={row?.leadDisplayId} />
              <InfoBit label="Lead type" value={row?.leadType} />
              <InfoBit label="Status before transfer" value={row?.oldLeadStatus} />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </DialogActions>
    </Dialog>
  );
}

export default function TransferHistoryPage() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [notice, setNotice] = useState(null);

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [cps, setCps] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedCp, setSelectedCp] = useState(null);

  const [batchId, setBatchId] = useState(null);
  const [batchRows, setBatchRows] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);

  const [rowDetail, setRowDetail] = useState(null);

  const openBatch = async (id) => {
    setBatchId(id);
    setBatchLoading(true);
    setBatchRows([]);
    try {
      const res = await getBatchTransferHistory(id);
      if (res.success) setBatchRows(res.data.rows);
      else setNotice({ type: "error", title: "Couldn't load batch", message: res.message });
    } catch (err) {
      setNotice({ type: "error", title: "Couldn't load batch", message: err.message });
    } finally {
      setBatchLoading(false);
    }
  };

  useEffect(() => {
    getAllUsers("Name Ascending").then(({ data }) => setUsers(data || [])).catch(() => {});
    getAllProjects().then(({ data }) => setProjects(data || [])).catch(() => {});
    supabase.from("CP").select("cpId, billingName").then(({ data }) => setCps(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setRows(null);
    if (tab === 0) loadAll();
  }, [tab]);

  const handleResult = (res) => {
    if (!res.success) {
      setNotice({ type: "error", title: "Couldn't load history", message: res.message });
      setRows([]);
      return;
    }
    setRows(res.data.rows);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await getAllTransferHistory({ limit: 100, offset: 0 });
      handleResult(res);
    } catch (err) {
      setNotice({ type: "error", title: "Couldn't load history", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const loadByUser = async () => {
    if (!selectedUser) {
      setNotice({ type: "warning", title: "Pick a user", message: "Select a user to see their transfer history." });
      return;
    }
    setLoading(true);
    try {
      handleResult(await getUserTransferHistory(selectedUser.userId));
    } catch (err) {
      setNotice({ type: "error", title: "Couldn't load history", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const loadByProject = async () => {
    if (!selectedProject) {
      setNotice({ type: "warning", title: "Pick a project", message: "Select a project to see its transfer history." });
      return;
    }
    setLoading(true);
    try {
      handleResult(await getProjectTransferHistory(selectedProject.projectId));
    } catch (err) {
      setNotice({ type: "error", title: "Couldn't load history", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const loadByCp = async () => {
    if (!selectedCp) {
      setNotice({ type: "warning", title: "Pick a CP", message: "Select a CP to see its source-change history." });
      return;
    }
    setLoading(true);
    try {
      handleResult(await getCpSourceTransferHistory(selectedCp.cpId));
    } catch (err) {
      setNotice({ type: "error", title: "Couldn't load history", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <PageHeader title="Transfer History" subtitle="A permanent, read-only log of every transfer this module has ever made." />

      <Tabs
        value={tab}
        onChange={(e, val) => setTab(val)}
        sx={{
          mb: 2,
          borderBottom: `2px solid ${COLORS.border}`,
          minHeight: "auto",
          "& .MuiTab-root": { fontSize: 13.5, fontWeight: 600, textTransform: "none", color: COLORS.textSecondary, minHeight: "auto", py: 1.2 },
          "& .Mui-selected": { color: `${COLORS.blue} !important` },
          "& .MuiTabs-indicator": { bgcolor: COLORS.blue },
        }}
      >
        {TABS.map((t) => (
          <Tab key={t} label={t} />
        ))}
      </Tabs>

      {tab === 1 && (
        <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="User" sx={{ width: 280 }}>
              <Autocomplete
                options={users}
                getOptionLabel={(u) => u.name || ""}
                value={selectedUser}
                onChange={(e, val) => setSelectedUser(val)}
                renderInput={(params) => <TextField {...params} placeholder="Choose a user" />}
                sx={fieldSx}
              />
            </Field>
            <PrimaryButton onClick={loadByUser}>Load</PrimaryButton>
          </Box>
        </Box>
      )}

      {tab === 2 && (
        <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="Project" sx={{ width: 280 }}>
              <Autocomplete
                options={projects}
                getOptionLabel={(p) => p.projectName || ""}
                value={selectedProject}
                onChange={(e, val) => setSelectedProject(val)}
                renderInput={(params) => <TextField {...params} placeholder="Choose a project" />}
                sx={fieldSx}
              />
            </Field>
            <PrimaryButton onClick={loadByProject}>Load</PrimaryButton>
          </Box>
        </Box>
      )}

      {tab === 3 && (
        <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="CP" sx={{ width: 280 }}>
              <Autocomplete
                options={cps}
                getOptionLabel={(c) => c.billingName || ""}
                value={selectedCp}
                onChange={(e, val) => setSelectedCp(val)}
                renderInput={(params) => <TextField {...params} placeholder="Choose a CP" />}
                sx={fieldSx}
              />
            </Field>
            <PrimaryButton onClick={loadByCp}>Load</PrimaryButton>
          </Box>
        </Box>
      )}

      <Box sx={{ ...cardSx, p: loading ? 3 : 0, overflow: "hidden" }}>
        {loading ? <CircularProgress size={24} /> : <HistoryTable rows={rows} onOpenBatch={openBatch} onOpenRow={setRowDetail} />}
      </Box>

      <BatchDetailDialog batchId={batchId} loading={batchLoading} rows={batchRows} onClose={() => setBatchId(null)} />
      <RowDetailDialog row={rowDetail} onClose={() => setRowDetail(null)} />
      <WorkTransferNotice notice={notice} onClose={() => setNotice(null)} />
    </Box>
  );
}
